import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { FlagDivider } from './FlagDivider';
import { BookOpen, Headphones, Play, Pause, Volume2, VolumeX, Loader2 } from 'lucide-react';
function AudioPlayer() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState(false);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateProgress = () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration && !isNaN(audio.duration)) {
        setProgress(audio.currentTime / audio.duration * 100);
      }
    };
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };
    const handleCanPlay = () => {
      setIsLoading(false);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };
    const handleError = () => {
      setError(true);
      setIsLoading(false);
      setIsPlaying(false);
    };
    const handleWaiting = () => {
      setIsLoading(true);
    };
    const handlePlaying = () => {
      setIsLoading(false);
      setIsPlaying(true);
    };
    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('playing', handlePlaying);
    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('playing', handlePlaying);
    };
  }, []);
  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio || error) return;
    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        setIsLoading(true);
        await audio.play();
        setIsPlaying(true);
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Audio playback error:', err);
      setIsLoading(false);
      setError(true);
    }
  };
  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  };
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const newTime = clickX / width * audio.duration;
    audio.currentTime = newTime;
  };
  const formatTime = (time: number) => {
    if (isNaN(time) || !isFinite(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  return <div className="w-full max-w-md mx-auto lg:mx-0 mb-6" data-id="element-36">
      <audio ref={audioRef} preload="auto" crossOrigin="anonymous" data-id="element-37">
        <source src="https://share.aielitesolutions.com/files/697d59fda99056c7a6730127/c5207922-db3e-4dac-bdfc-c89f3bf1eea8" type="audio/mpeg" data-id="element-38" />
        <source src="https://share.aielitesolutions.com/files/697d59fda99056c7a6730127/c5207922-db3e-4dac-bdfc-c89f3bf1eea8" type="audio/mp3" data-id="element-39" />
        Your browser does not support the audio element.
      </audio>

      <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-4" data-id="element-40">
        <div className="flex items-center gap-2 mb-3" data-id="element-41">
          <Headphones size={16} className="text-[#FFCC00]" data-id="element-42" />
          <span className="text-xs text-zinc-400 uppercase tracking-wider font-medium" data-id="element-43">
            {error ? 'Audio no disponible' : 'Escucha un fragmento'}
          </span>
        </div>

        <div className="flex items-center gap-3" data-id="element-44">
          {/* Play/Pause Button */}
          <button onClick={togglePlay} disabled={error} className={`w-10 h-10 flex items-center justify-center rounded-full transition-all transform flex-shrink-0 ${error ? 'bg-zinc-700 cursor-not-allowed' : 'bg-[#FFCC00] hover:bg-[#e6b800] hover:scale-105'}`} data-id="element-45">
            {isLoading ? <Loader2 size={18} className="text-[#0a0a0a] animate-spin" data-id="element-46" /> : isPlaying ? <Pause size={18} className="text-[#0a0a0a]" fill="#0a0a0a" data-id="element-47" /> : <Play size={18} className="text-[#0a0a0a] ml-0.5" fill="#0a0a0a" data-id="element-48" />}
          </button>

          {/* Progress Bar */}
          <div className="flex-1" data-id="element-49">
            <div onClick={handleProgressClick} className="h-2 bg-zinc-700 rounded-full cursor-pointer overflow-hidden group" data-id="element-50">
              <div className="h-full bg-gradient-to-r from-[#FFCC00] via-[#00247D] to-[#CF142B] rounded-full transition-all duration-100" style={{
              width: `${progress}%`
            }} data-id="element-51" />
            </div>
            <div className="flex justify-between mt-1.5 text-xs text-zinc-500" data-id="element-52">
              <span data-id="element-53">{formatTime(currentTime)}</span>
              <span data-id="element-54">{formatTime(duration)}</span>
            </div>
          </div>

          {/* Mute Button */}
          <button onClick={toggleMute} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-white transition-colors flex-shrink-0" data-id="element-55">
            {isMuted ? <VolumeX size={18} data-id="element-56" /> : <Volume2 size={18} data-id="element-57" />}
          </button>
        </div>
      </div>
    </div>;
}
export function HeroSection() {
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section id="hero" className="relative flex flex-col items-center justify-center overflow-hidden bg-[#0a0a0a] pt-20 sm:pt-24 pb-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8" data-id="element-58">
      {/* Background subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0a0a0a] to-[#111] opacity-80 z-0" data-id="element-59" />

      <div className="relative z-10 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center" data-id="element-60">
        {/* Text Content */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="text-center lg:text-left order-2 lg:order-1" data-id="element-61">
          <div className="inline-block mb-4 sm:mb-6 px-3 py-1 border border-zinc-800 rounded-full bg-zinc-900/50 backdrop-blur-sm" data-id="element-62">
            <span className="text-zinc-400 text-xs sm:text-sm font-medium tracking-wide uppercase" data-id="element-63">
              AudioLibro + eBook
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-4 sm:mb-6" data-id="element-64">
            VENEZUELA: <br data-id="element-65" />
            <span className="text-zinc-400 italic font-normal text-xl sm:text-3xl lg:text-4xl xl:text-5xl block mt-1 sm:mt-2" data-id="element-66">
              La verdad que el mundo no quiso ver.
            </span>
          </h1>

          <p className="text-sm sm:text-lg lg:text-xl text-zinc-300 mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0 font-light" data-id="element-67">
            Si eres venezolano te sentirás identificado con él y tu madre
            tierra.
          </p>

          {/* Audio Player */}
          <AudioPlayer data-id="element-68" />

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start items-center mb-6 sm:mb-8" data-id="element-69">
            <button onClick={scrollToPricing} className="bg-[#FFCC00] hover:bg-[#e6b800] text-[#0a0a0a] font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all transform hover:scale-105 shadow-lg shadow-yellow-900/20 w-full sm:w-auto text-base sm:text-lg" data-id="element-70">
              Comprar Ahora — $14.97
            </button>
            <div className="flex items-center gap-3 sm:gap-4 text-zinc-400 text-xs sm:text-sm" data-id="element-71">
              <span className="flex items-center gap-1" data-id="element-72">
                <BookOpen size={14} className="sm:w-4 sm:h-4" data-id="element-73" /> eBook PDF
              </span>
              <span className="w-1 h-1 bg-zinc-600 rounded-full" data-id="element-74"></span>
              <span className="flex items-center gap-1" data-id="element-75">
                <Headphones size={14} className="sm:w-4 sm:h-4" data-id="element-76" /> Audio MP3
              </span>
            </div>
          </div>

          <FlagDivider className="max-w-[150px] sm:max-w-[200px] mx-auto lg:mx-0 opacity-70" data-id="element-77" />
        </motion.div>

        {/* Book Image */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: 'easeOut'
      }} className="order-1 lg:order-2 flex justify-center" data-id="element-78">
          <div className="relative group" data-id="element-79">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#FFCC00] via-[#00247D] to-[#CF142B] rounded-lg blur opacity-20 group-hover:opacity-40 transition duration-1000" data-id="element-80"></div>
            <img src="https://cdn.magicpatterns.com/uploads/d9zPQuNrTr8BSQu1zYBo2M/heylloyd-image-1769816278400.png" alt="Venezuela: La verdad que el mundo no quiso ver - Libro" className="relative w-full max-w-[280px] sm:max-w-sm lg:max-w-md rounded-lg shadow-2xl shadow-black border border-zinc-800" data-id="element-81" />
          </div>
        </motion.div>
      </div>
    </section>;
}